import React, { useState, useRef } from "react";
import "./css/Employment.css";

const POSITIONS = [
  {
    title: "Developer",
    dept: "Engineering",
    desc: "Build and maintain cutting-edge applications across the Vyral ecosystem.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
  },
  {
    title: "Graphic Artist",
    dept: "Creative",
    desc: "Design stunning visuals and brand assets for our growing portfolio of apps.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
      </svg>
    ),
  },
  {
    title: "Social Media Ad Management",
    dept: "Marketing",
    desc: "Plan and execute paid ad campaigns across social platforms for our brands.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
  },
  {
    title: "Sales",
    dept: "Revenue",
    desc: "Drive growth by building relationships and closing deals across our divisions.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
  },
];

const EMPTY = {
  firstName: "", lastName: "", email: "", phone: "",
  position: "", experience: "", skills: "", portfolio: "",
  availability: "", coverLetter: "",
};

const Employment = () => {
  const [form, setForm]               = useState(EMPTY);
  const [submitted, setSubmitted]     = useState(false);
  const formRef                       = useRef(null);

  const selectPosition = (title) => {
    setForm(prev => ({ ...prev, position: title }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = `Employment Application - ${form.position}`;
    const body = `Dear Vyral Media Group,\n\nI am applying for the ${form.position} position.\n\nName: ${form.firstName} ${form.lastName}\nEmail: ${form.email}\nPhone: ${form.phone}\nExperience: ${form.experience}\nAvailability: ${form.availability}\n${form.portfolio ? `Portfolio: ${form.portfolio}\n` : ""}\nSkills:\n${form.skills}\n\nCover Letter:\n${form.coverLetter}\n\nBest regards,\n${form.firstName} ${form.lastName}`;
    window.location.href = `mailto:info@vyralmediagroup.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
    setForm(EMPTY);
  };

  return (
    <div className="emp-page">

      {/* Hero */}
      <section className="emp-hero">
        <div className="emp-hero-inner">
          <p className="emp-eyebrow">We Are Hiring</p>
          <h1>Build the Future<br/>With Us</h1>
          <p className="emp-sub">
            Join the team creating the next generation of social media, entertainment, and technology platforms.
          </p>
        </div>
      </section>

      <div className="emp-body">

        {/* Position tiles */}
        <section className="emp-positions">
          <h2 className="emp-positions-title">Open Roles</h2>
          <div className="emp-tiles">
            {POSITIONS.map((p, i) => (
              <button
                key={p.title}
                className={`emp-tile${form.position === p.title ? " selected" : ""}`}
                onClick={() => selectPosition(p.title)}
              >
                <div className="emp-tile-top">
                  <span className="emp-tile-num">0{i + 1}</span>
                  <span className="emp-tile-dept">{p.dept}</span>
                </div>
                <div className="emp-tile-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <span className="emp-tile-cta">
                  {form.position === p.title ? "Selected" : "Apply for this role"}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="emp-divider" />

        {/* Form */}
        <section className="emp-form-section" ref={formRef}>
          <div className="emp-form-header">
            <h2>Submit Your Application</h2>
            <p>
              {form.position
                ? `Applying for: ${form.position}`
                : "Select a role above, or choose from the dropdown below"}
            </p>
          </div>

          {submitted && (
            <div className="emp-success">
              Your email client should open shortly. Please attach your resume before sending.
            </div>
          )}

          <form onSubmit={onSubmit} className="emp-form">

            {/* Personal */}
            <fieldset className="emp-fieldset">
              <legend>Personal Info</legend>
              <div className="emp-grid-2">
                <div className="emp-field">
                  <label>First Name <span>*</span></label>
                  <input name="firstName" value={form.firstName} onChange={onChange} placeholder="Jane" required />
                </div>
                <div className="emp-field">
                  <label>Last Name <span>*</span></label>
                  <input name="lastName" value={form.lastName} onChange={onChange} placeholder="Doe" required />
                </div>
                <div className="emp-field">
                  <label>Email <span>*</span></label>
                  <input type="email" name="email" value={form.email} onChange={onChange} placeholder="jane@example.com" required />
                </div>
                <div className="emp-field">
                  <label>Phone <span>*</span></label>
                  <input type="tel" name="phone" value={form.phone} onChange={onChange} placeholder="+1 555 000 0000" required />
                </div>
              </div>
            </fieldset>

            {/* Role */}
            <fieldset className="emp-fieldset">
              <legend>Role Details</legend>
              <div className="emp-grid-2">
                <div className="emp-field">
                  <label>Position <span>*</span></label>
                  <select name="position" value={form.position} onChange={onChange} required>
                    <option value="">Select a position</option>
                    {POSITIONS.map(p => <option key={p.title} value={p.title}>{p.title}</option>)}
                  </select>
                </div>
                <div className="emp-field">
                  <label>Experience <span>*</span></label>
                  <select name="experience" value={form.experience} onChange={onChange} required>
                    <option value="">Select level</option>
                    <option value="0-1">0 - 1 years</option>
                    <option value="2-3">2 - 3 years</option>
                    <option value="4-5">4 - 5 years</option>
                    <option value="6-10">6 - 10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
                <div className="emp-field">
                  <label>Availability <span>*</span></label>
                  <select name="availability" value={form.availability} onChange={onChange} required>
                    <option value="">Select availability</option>
                    <option value="immediate">Immediate</option>
                    <option value="2-weeks">2 weeks notice</option>
                    <option value="1-month">1 month notice</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div className="emp-field">
                  <label>Portfolio / Website</label>
                  <input type="url" name="portfolio" value={form.portfolio} onChange={onChange} placeholder="https://yourportfolio.com" />
                </div>
              </div>
            </fieldset>

            {/* About you */}
            <fieldset className="emp-fieldset">
              <legend>About You</legend>
              <div className="emp-field">
                <label>Relevant Skills <span>*</span></label>
                <textarea name="skills" value={form.skills} onChange={onChange} placeholder="List your key skills, tools, and technologies..." rows="3" required />
              </div>
              <div className="emp-field">
                <label>Cover Letter <span>*</span></label>
                <textarea name="coverLetter" value={form.coverLetter} onChange={onChange} placeholder="Why do you want to join Vyral Media Group? What makes you a great fit?" rows="5" required />
              </div>
            </fieldset>

            <div className="emp-form-footer">
              <p className="emp-resume-note">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                Attach your resume directly to the email before sending.
              </p>
              <button type="submit" className="emp-submit">
                Open Email Application
              </button>
            </div>

          </form>
        </section>

      </div>
    </div>
  );
};

export default Employment;
